<template>
  <div class="counselor-slider" :class="resolvedRole">
    <div
      class="counselor-slider__wrapper"
      v-if="!isLoading && preparedUsers && preparedUsers.length"
    >
      <div class="counselor-slider__mobile-view" v-if="isMobile || isTablet">
        <CounselorCard
          v-for="counselor in visibleCounselors"
          :key="counselor.id"
          v-bind="counselorProps(counselor)"
        />
      </div>
      <div class="counselor-slider__desktop-view" v-if="!isMobile && !isTablet">
        <Slider :classes="sliderClasses">
          <CounselorCard
            v-for="counselor in preparedUsers"
            :key="counselor.id"
            v-bind="counselorProps(counselor)"
          />
        </Slider>
      </div>
    </div>
    <button
      class="btn n-btn counselor-slider__show-more"
      v-if="!isExpanded && !isLoading && users && users.length"
      @click="isExpanded = true"
    >
      {{ showMoreText }}
    </button>
    <loader :loading="isLoading" />
    <verified-modal v-if="isTherapist" />
  </div>
</template>

<script>
import Loader from 'Components/loader/Loader.vue';
import Slider from 'Components/Slider.vue';
import CounselorCard from 'Main/components/counselor-card/CounselorCard.vue';
import { useCityTermLocalStorage } from 'Main/composables/useCityTermLocalStorage';
import { useIsMobile } from 'Main/composables/useIsMobile';
import {
  ROLE,
  resolveApiRole,
  useRoleTermLocalStorage,
} from 'Main/composables/useRoleTermLocalStorage';
import VerifiedModal from 'Main/verified-by-lavendla/components/VerifiedModal.vue';
import {
  getUserBookedInTheLatest72Hours,
  getUserReturningCustomer,
} from 'Services/counselor-grid-user-service';
import { mapState } from 'vuex';

export default {

  beforeUnmount() {
    window.removeEventListener(
      'city-storage-updated-event',
      this.loadCounselors
    );
  },

  components: {
    CounselorCard,
    Loader,
    Slider,
    VerifiedModal,
  },

  computed: {
    ...mapState('users', ['users', 'isLoading']),

    counselorProps() {
      return (counselor) => ({
        cityLabel: this.cityTerm ? this.cityTerm.name : null,
        competences: this.competences,
        counselor,
        isLawyer: this.resolvedRole === ROLE.LAWYER,
        isTherapist: this.isTherapist,
        role: this.resolvedRole,
        schedule: counselor.schedule,
        selectedCompetences: this.competences || '',
        showCompetenceAreas: this.isTherapist || this.resolvedRole === ROLE.LAWYER,
        showPhoneNumber: this.showPhoneNumber && ! this.isTherapist,
      });
    },

    isTherapist() {
      return this.resolvedRole === ROLE.THERAPY;
    },

    preparedUsers() {
      if (!this.users) {
        return [];
      }

      if (this.isTherapist) {
        return this.users.map((user) => {
          return {
            ...user,
            booked_in_the_latest_72_hours: getUserBookedInTheLatest72Hours(user),
            returning_customer: getUserReturningCustomer(user),
          };
        });
      }

      return this.users;
    },

    // Drives both the advisors API call and the card presentation so the
    // fetched advisor type and how the cards render never diverge.
    resolvedRole() {
      return resolveApiRole(this.role, this.roleTerm);
    },

    sliderClasses() {
      return this.slimTemplate ? 'size-2' : 'size-4';
    },

    visibleCounselors() {
      if (this.isExpanded) {
        return this.preparedUsers;
      }

      if (this.isTablet) {
        return this.preparedUsers.slice(0, 4);
      }

      return this.preparedUsers.slice(0, 3);
    },
  },

  data() {
    const {
      city,
      role,
      competences,
      siteId,
      adminMarket,
      slimTemplate,
      phoneNumber: phoneNumberFromData,
    } = this.$mountEl.dataset;

    return {
      adminMarket: JSON.parse(adminMarket) || null,
      city: city && JSON.parse(city) ? JSON.parse(city).slug : null,
      competences,
      isExpanded: false,
      role,
      showMoreText: window.theme_vars.counselorSlider.showMoreButtonText,
      showPhoneNumber: !!phoneNumberFromData,
      siteId: JSON.parse(siteId) || null,
      slimTemplate,
    };
  },

  methods: {
    /**
     * Get timeslots!
     */
    loadCounselors() {
      this.cityTerm = this.getCityTermFromLocalStorage();
      const tzOffset = new Date().getTimezoneOffset() * 60000; // offset in milliseconds
      const localISOTime = new Date(Date.now() - tzOffset)
        .toISOString()
        .slice(0, -1);
      const tomorrow = localISOTime.slice(0, 10);
      const apiCity = this.cityTerm ? this.cityTerm.slug : this.city;
      const apiPath = !apiCity ? 'users/getTopUsers' : 'users/getUsers';
      this.$store.dispatch(apiPath, {
        adminMarket: this.adminMarket,
        city: apiCity,
        competence: this.competences,
        role: this.resolvedRole,
        siteId: this.siteId,
        startDate: tomorrow,
      });
    },
  },

  mounted() {
    this.saveRoleTermToLocalStorage(this.role);
    this.loadCounselors();
    window.addEventListener('city-storage-updated-event', this.loadCounselors);
  },
  name: 'CounselorSlider',

  setup() {
    const { isMobile, isTablet } = useIsMobile();
    const {
      cityTerm,
      saveCityTermToLocalStorage,
      getCityTermFromLocalStorage,
    } = useCityTermLocalStorage();
    const { roleTerm, saveRoleTermToLocalStorage } = useRoleTermLocalStorage();
    return {
      cityTerm,
      getCityTermFromLocalStorage,
      isMobile,
      isTablet,
      roleTerm,
      saveCityTermToLocalStorage,
      saveRoleTermToLocalStorage,
    };
  },
};
</script>
